import omit from 'lodash/omit'
import moment from 'moment'
import { validate as uuidValidate } from 'uuid'
import {
  FinancialsEntry,
  FundingRequestStatus,
  OngoingReportingType,
  QUARTERS_MAPPING,
  isBalanceSheetType,
} from '../../common/interfaces/bbc'
import { CITATION_MARKER_PATTERN } from '../constants/atlasai'
import { LOAN_TYPES, REPORTING_DOCUMENT_TYPES } from '../constants/client'
import { LOGO_DEV_IMAGE_HOST, LOGO_DEV_PUBLIC_KEY } from '../constants/logoDev'
import { ActivityStatus, ActivityType } from '../interfaces/activity'
import { IAtlasAIMention } from '../interfaces/atlasai'
import {
  BOX_SIGN_STATUS_LABEL,
  BoxSignStatus,
  BoxSignStatusLabel,
  MANUALLY_SEND_LABEL,
} from '../interfaces/box'
import { allClientTermsInfo, FieldType } from '../interfaces/client'
import {
  DUE_DILIGENCE_TABLE_ITEM_FIELD_LABELS,
  DUE_DILIGENCE_TABLE_ITEM_REQUIRED_FIELDS,
  DueDiligenceTableItemType,
  IDueDiligenceExecutive,
  IDueDiligenceInventoryLocation,
} from '../interfaces/dueDiligence'
import { MentionType } from '../interfaces/mentions'
import { type TaskStatusValue } from '../interfaces/task'
import { type ITermLoan } from '../interfaces/termLoan'

/**
 * Whether a required report can be deleted based on document type and client deal type.
 */
export const canDeleteRequiredReport = (
  documentName: string,
  loanType?: string | null,
): boolean => {
  if (documentName === REPORTING_DOCUMENT_TYPES.apAging) return false
  if (
    documentName === REPORTING_DOCUMENT_TYPES.arAging ||
    documentName === REPORTING_DOCUMENT_TYPES.arGeneralLedger
  )
    return loanType === LOAN_TYPES.inventory
  if (
    documentName === REPORTING_DOCUMENT_TYPES.inventoryDetail ||
    documentName === REPORTING_DOCUMENT_TYPES.salesBySKU ||
    documentName === REPORTING_DOCUMENT_TYPES.eop
  )
    return loanType === LOAN_TYPES.ar
  return true
}

/**
 * Escapes HTML special characters to prevent XSS when inserting untrusted data into HTML.
 */
export const escapeHtml = (text?: string | number | null): string => {
  if (text === undefined || text === null) return ''
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * The inverse of `escapeHtml`, for HTML headed somewhere that shows text literally
 * (a push banner, a Salesforce comparison). Numeric references first, &amp; last so
 * "&amp;lt;" decodes exactly one level.
 */
export const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, '\u00a0')
    .replace(/&amp;/g, '&')

/**
 * `Deal Flow — 2026!` → `deal-flow-2026`
 *
 * Shared rather than server-side because the channel dialog previews the handle you
 * are about to get. A client-side copy of these rules is a copy that drifts, and the
 * whole point of the preview is that it matches what `CreateChannel` will store.
 */
export const slugify = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)

// Allow only well-formed CSS color tokens (hex, rgb/rgba, or a bare keyword)
// before interpolating into a style attribute, so a crafted color string can't
// break out of the attribute or inject a data-exfiltrating background-image.
const HEX_COLOR = /^#[0-9a-fA-F]{3,8}$/
const RGB_COLOR = /^rgba?\(\s*[\d.\s,%/]+\)$/
const KEYWORD_COLOR = /^[a-zA-Z]+$/

export const safeCssColor = (value: string | undefined | null, fallback: string): string => {
  const trimmed = (value || '').trim()
  if (!trimmed) {
    return fallback
  }
  return HEX_COLOR.test(trimmed) || RGB_COLOR.test(trimmed) || KEYWORD_COLOR.test(trimmed)
    ? trimmed
    : fallback
}

// Only allow link targets whose scheme is safe to follow from an email client
// before interpolating them into an href. A crafted markdown link such as
// [x](javascript:alert(1)) would otherwise inject script into the generated
// HTML. Relative/anchor links (no scheme) are left untouched; anything with an
// unsafe scheme (javascript:, data:, vbscript:, …) is neutralised to a no-op.
const URL_SCHEME = /^[a-z][a-z0-9+.-]*:/i
const SAFE_URL_SCHEME = /^(?:https?|mailto|tel):/i

export const safeEmailUrl = (value: string | undefined | null): string => {
  const trimmed = (value || '').trim()
  // Strip control characters/whitespace that could hide an unsafe scheme
  // (e.g. "java\tscript:") before inspecting it.
  const normalized = trimmed.replace(/[\s\p{Cc}]+/gu, '')
  if (!URL_SCHEME.test(normalized) || SAFE_URL_SCHEME.test(normalized)) {
    return trimmed
  }
  return '#'
}

export const markdownToEmailHtml = (md: string): string => {
  if (!md) return ''

  let text = md.replace(CITATION_MARKER_PATTERN, '')

  const codeBlocks: string[] = []
  text = text.replace(/```(?:\w*)\n?([\s\S]*?)```/g, (_, code) => {
    const idx = codeBlocks.length
    codeBlocks.push(escapeHtml(code.trim()))
    return `\n%%CB_${idx}%%\n`
  })

  const tableBlocks: string[] = []
  text = text.replace(/(?:^|\n)((?:\|.+\|\s*\n)+)/g, (_, tableBlock: string) => {
    const rows = tableBlock.trim().split('\n')
    if (rows.length < 2) return tableBlock
    const isSeparator = (r: string) => /^\|[\s:|-]+\|$/.test(r.trim())
    if (!isSeparator(rows[1])) return tableBlock

    const parseRow = (r: string) =>
      r
        .trim()
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((c) => c.trim())

    const headerCells = parseRow(rows[0])
    const cellStyle = 'border:1px solid #e2e8f0;padding:8px 12px;text-align:left;'
    const headerStyle = `${cellStyle}background:#f7fafc;font-weight:600;`

    let tableHtml =
      '<table style="border-collapse:collapse;width:100%;margin:8px 0;font-size:14px;"><thead><tr>'
    for (const cell of headerCells) {
      tableHtml += `<th style="${headerStyle}">${escapeHtml(cell)}</th>`
    }
    tableHtml += '</tr></thead><tbody>'

    for (let i = 2; i < rows.length; i++) {
      if (isSeparator(rows[i])) continue
      const cells = parseRow(rows[i])
      tableHtml += '<tr>'
      for (let j = 0; j < headerCells.length; j++) {
        tableHtml += `<td style="${cellStyle}">${escapeHtml(cells[j] || '')}</td>`
      }
      tableHtml += '</tr>'
    }

    tableHtml += '</tbody></table>'
    const idx = tableBlocks.length
    tableBlocks.push(tableHtml)
    return `\n%%TB_${idx}%%\n`
  })

  text = escapeHtml(text)

  const lines = text.split('\n')
  const out: string[] = []
  let inList = false
  let listTag = 'ul'

  const closeList = () => {
    if (inList) {
      out.push(`</${listTag}>`)
      inList = false
    }
  }

  for (const line of lines) {
    const cbMatch = line.match(/^%%CB_(\d+)%%$/)
    if (cbMatch) {
      closeList()
      out.push(
        `<div style="background:#f4f4f5;border-radius:6px;padding:12px 16px;margin:8px 0;font-family:monospace;font-size:13px;white-space:pre-wrap;overflow-x:auto;color:#1A202C;">${codeBlocks[parseInt(cbMatch[1], 10)]}</div>`,
      )
      continue
    }

    const tbMatch = line.match(/^%%TB_(\d+)%%$/)
    if (tbMatch) {
      closeList()
      out.push(tableBlocks[parseInt(tbMatch[1], 10)])
      continue
    }

    const h3 = line.match(/^###\s+(.+)$/)
    if (h3) {
      closeList()
      out.push(
        `<h3 style="font-size:16px;font-weight:700;margin:16px 0 8px;color:#1A202C;">${h3[1]}</h3>`,
      )
      continue
    }
    const h2 = line.match(/^##\s+(.+)$/)
    if (h2) {
      closeList()
      out.push(
        `<h2 style="font-size:18px;font-weight:700;margin:20px 0 8px;color:#1A202C;">${h2[1]}</h2>`,
      )
      continue
    }
    const h1 = line.match(/^#\s+(.+)$/)
    if (h1) {
      closeList()
      out.push(
        `<h1 style="font-size:22px;font-weight:700;margin:24px 0 8px;color:#1A202C;">${h1[1]}</h1>`,
      )
      continue
    }

    if (/^-{3,}$/.test(line.trim())) {
      closeList()
      out.push('<hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0;"/>')
      continue
    }

    const ul = line.match(/^\s*[-*]\s+(.+)$/)
    if (ul) {
      if (!inList || listTag !== 'ul') {
        closeList()
        out.push('<ul style="margin:8px 0;padding-left:24px;">')
        inList = true
        listTag = 'ul'
      }
      out.push(`<li style="margin:4px 0;">${ul[1]}</li>`)
      continue
    }

    const ol = line.match(/^\s*\d+\.\s+(.+)$/)
    if (ol) {
      if (!inList || listTag !== 'ol') {
        closeList()
        out.push('<ol style="margin:8px 0;padding-left:24px;">')
        inList = true
        listTag = 'ol'
      }
      out.push(`<li style="margin:4px 0;">${ol[1]}</li>`)
      continue
    }

    if (line.trim() === '') {
      if (!inList) out.push('<br/>')
      continue
    }

    closeList()
    out.push(`<p style="margin:4px 0;line-height:1.5;">${line}</p>`)
  }

  closeList()

  let html = out.join('')

  html = html.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<i>$1</i>')
  html = html.replace(
    /`([^`]+)`/g,
    '<code style="background:#f4f4f5;padding:2px 6px;border-radius:3px;font-family:monospace;font-size:13px;">$1</code>',
  )
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_match, label: string, url: string) =>
      `<a href="${safeEmailUrl(url)}" style="color:#0066f5;text-decoration:none;">${label}</a>`,
  )

  return html
}

/**
 * Months rolled up into the periods `periodOf` names. A balance sheet is a
 * snapshot, so a period keeps the first month it is given; flows add up.
 */
const rollUpMonths = (
  months: FinancialsEntry[],
  statementType: OngoingReportingType | undefined,
  periodOf: (month: moment.Moment) => string | null,
): FinancialsEntry[] => {
  const rolledUp = months.reduce(
    (accumulator, entry) => {
      const key = periodOf(moment.utc(entry.period))
      if (!key) {
        return accumulator
      }
      // A "20" cell is twenty, not text to append to.
      const amount = Number(entry.amount) || 0
      if (!accumulator.hasOwnProperty(key)) {
        accumulator[key] = { period: key, amount }
      } else if (!isBalanceSheetType(statementType)) {
        accumulator[key].amount += amount
      }
      return accumulator
    },
    {} as Record<string, FinancialsEntry>,
  )

  return Object.values(rolledUp)
}

export const monthsToQuarterly = (
  months: FinancialsEntry[],
  statementType?: OngoingReportingType,
): FinancialsEntry[] =>
  rollUpMonths(months, statementType, (period) => {
    const quarter = getTypedObjectKeys(QUARTERS_MAPPING).find((key) =>
      QUARTERS_MAPPING[key].includes(period.month()),
    )
    return quarter ? `${quarter} ${period.year()}` : null
  })

export const monthsToAnnually = (
  months: FinancialsEntry[],
  statementType?: OngoingReportingType,
): FinancialsEntry[] => rollUpMonths(months, statementType, (period) => period.year().toString())

export const textToTitleCase = (text: string, splitBy = ' ') =>
  text
    .toLowerCase()
    .split(splitBy)
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(splitBy)

export const formatCamelCaseToRegular = (value: string) => {
  return value.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => {
    return str.toUpperCase()
  })
}

export const getClientTermTitle = (termName: string) => {
  const camelCaseTerm = snakeToCamelCase(termName)
  const termInfo = allClientTermsInfo.find((term) => term.value === camelCaseTerm)
  return termInfo?.label || formatCamelCaseToRegular(camelCaseTerm)
}

export interface IInterestRateTierRow {
  balanceThreshold: number
  interestRateBase: number
  interestRateAbovePrime: number
}

// Tier payloads are stored from `list_of: 'any_object'` input, so numeric strings are accepted too.
const toFiniteNumber = (value: unknown): number | null => {
  const isNumeric = typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')
  const parsed = isNumeric ? Number(value) : NaN
  return Number.isFinite(parsed) ? parsed : null
}

const toInterestRateTierRow = (value: unknown): IInterestRateTierRow | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const tier = value as Record<string, unknown>
  const balanceThreshold = toFiniteNumber(tier.balanceThreshold)
  const interestRateBase = toFiniteNumber(tier.interestRateBase)
  const interestRateAbovePrime = toFiniteNumber(tier.interestRateAbovePrime)
  if (balanceThreshold === null || interestRateBase === null || interestRateAbovePrime === null) {
    return null
  }
  return { balanceThreshold, interestRateBase, interestRateAbovePrime }
}

/**
 * Parses a stored tier list. Callers pair old and new tiers by index, so one invalid element
 * rejects the whole list rather than shifting later tiers into the wrong position.
 */
export const parseInterestRateTierRows = (
  json: string | null | undefined,
): IInterestRateTierRow[] => {
  if (!json) return []
  try {
    const rows: unknown = JSON.parse(json)
    if (!Array.isArray(rows)) return []
    const tiers = rows.map(toInterestRateTierRow)
    return tiers.every((tier): tier is IInterestRateTierRow => tier !== null) ? tiers : []
  } catch {
    return []
  }
}

export const findClientTermInfo = (termName: string) => {
  return allClientTermsInfo.find(
    (term) => term.value === termName || (term.dbName || camelToSnakeCase(term.value)) === termName,
  )
}

export const getUniqueType = (type: string, existsTypes: string[], increment?: number): string => {
  const newType = increment ? `${type}-${increment}` : type
  existsTypes = existsTypes.map((type) => type.toLowerCase())
  if (!existsTypes.includes(newType.toLowerCase())) {
    return newType
  }

  return getUniqueType(type, existsTypes, increment ? ++increment : 1)
}

export const buildTypeFromName = (name: string, increment?: number) =>
  `${name.replace(/[^\w]/g, '-').replace(/(-){2,}/g, '-')}${increment ? `-${increment}` : ''}`

export const sortByOrder = <T extends { order?: number }>(items: T[]): T[] => {
  return [...items].sort((a, b) => {
    if (a.order === undefined && b.order === undefined) return 0
    if (a.order === undefined) return 1
    if (b.order === undefined) return -1
    return a.order - b.order
  })
}
export const isEmpty = (value: unknown) => !value && value !== 0

/** A non-blank string value, trimmed, or null — for reading fields off untyped payloads. */
export const trimmedOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null

// Entity ids are `uuid` columns, so a malformed id reaching a WHERE/IN clause is
// a Postgres type error (22P02) that fails the whole query — not a no-match.
// Guard before querying with an id that came from outside (a URL, an import).
// Wraps uuid's own validator, which takes a required string.
export const isUuid = (value: string | null | undefined): boolean => !!value && uuidValidate(value)

const decodeBase64 = (base64: string): string => {
  // `atob` is a browser global; `Buffer` is the Node/server equivalent. This
  // helper lives in the shared `common` package, so support both runtimes
  // instead of assuming the browser environment.
  if (typeof atob === 'function') {
    return atob(base64)
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64, 'base64').toString('binary')
  }
  throw new Error('No base64 decoder available in this environment')
}

export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/')

  const rawData = decodeBase64(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export const camelToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

export const snakeToCamelCase = (str: string) =>
  str.toLowerCase().replace(/[-_]([a-z0-9])/g, (_, char) => char.toUpperCase())

export const snakeToTitleCase = (str: string) =>
  str
    .split('_')
    .map((word) => textToTitleCase(word))
    .join(' ')

export const escapeLikeString = (raw: string, escapeChar = '\\'): string =>
  raw.replace(/[\\%_]/g, (match) => escapeChar + match)

/** Comma-separated text → trimmed entries, blanks dropped. An entry can't itself contain a comma. */
export const splitCommaSeparated = (value: string | null | undefined): string[] =>
  (value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)

export const addHttp = (url: string) => {
  if (!/^(?:f|ht)tps?:\/\//i.test(url)) {
    return `https://${url}`
  }
  return url
}

export const getDueDiligenceTableItemMissingFields = (
  type: DueDiligenceTableItemType,
  item: object,
) => {
  const missingFields: string[] = []
  const requiredFields: string[] = DUE_DILIGENCE_TABLE_ITEM_REQUIRED_FIELDS[type] || []

  requiredFields.forEach((field) => {
    if (!(item as Record<string, unknown>)[field]) {
      missingFields.push(
        DUE_DILIGENCE_TABLE_ITEM_FIELD_LABELS[
          field as keyof typeof DUE_DILIGENCE_TABLE_ITEM_FIELD_LABELS
        ],
      )
    }
  })

  switch (type) {
    case DueDiligenceTableItemType.Executive:
      {
        const executive = item as IDueDiligenceExecutive

        if (
          ![
            executive.street,
            executive.city,
            executive.state,
            executive.postalCode,
            executive.country,
          ].filter(Boolean).length
        ) {
          missingFields.push('Address')
        }

        if (
          executive.isConvictedFelony === null ||
          executive.isDefaultedFinancialObligation === null ||
          executive.isPersonalBankruptcy === null
        ) {
          missingFields.push('Personal history')
        }

        if (
          (executive.isConvictedFelony && !executive.isConvictedFelonyComment) ||
          (executive.isDefaultedFinancialObligation &&
            !executive.isDefaultedFinancialObligationComment) ||
          (executive.isPersonalBankruptcy && !executive.isPersonalBankruptcyComment)
        ) {
          missingFields.push('Personal history detail')
        }
      }
      break
    case DueDiligenceTableItemType.InventoryLocation:
      {
        const inventoryLocation = item as IDueDiligenceInventoryLocation
        if (
          ![
            inventoryLocation.street,
            inventoryLocation.city,
            inventoryLocation.state,
            inventoryLocation.postalCode,
            inventoryLocation.country,
          ].filter(Boolean).length
        ) {
          missingFields.push('Address')
        }
      }
      break
  }

  return missingFields
}

export const sleep = async (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const retryPromise = async (
  fn: () => Promise<any>,
  retries: number,
  baseDelayMs: number = 500,
  attempt: number = 0,
): Promise<any> => {
  try {
    const data = await fn()
    return data
  } catch (e) {
    if (retries <= 1) {
      return Promise.reject(e)
    }

    const delayMs = baseDelayMs * Math.pow(2, attempt)
    await sleep(delayMs)
    return retryPromise(fn, retries - 1, baseDelayMs, attempt + 1)
  }
}

export const humanReadableFileSize = (
  sizeInBytes: number,
  precise: boolean = false,
  denominator: number = 1024,
): [string, string] => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const absoluteBytes = Math.abs(sizeInBytes)

  if (absoluteBytes === 0) return ['0', 'B']

  const rawExp = Math.floor(Math.log(absoluteBytes) / Math.log(denominator))
  const exp = Math.min(Math.max(rawExp, 0), units.length - 1)
  const sign = sizeInBytes < 0 ? '-' : ''

  return [sign + (absoluteBytes / Math.pow(denominator, exp)).toFixed(precise ? 2 : 0), units[exp]]
}

export const isDefined = <T>(value: T | null | undefined): value is NonNullable<T> => {
  return value !== null && value !== undefined
}

export const htmlToText = (html: string) => {
  return html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ')
}

export const removeSpaceWithReplace = (str: string) => str.replace(/\s+/g, '')

/**
 * Decoded byte count of a base64 body: 4 characters carry 3 bytes, less '=' padding. Exact, and
 * measures without materializing — allocating a multi-megabyte Buffer just to size it is what
 * failed under memory pressure.
 */
export const decodedBase64Size = (body: string): number => {
  const padding = body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0
  return Math.floor(body.length / 4) * 3 - padding
}

/**
 * Normalizes a name/text for case- and whitespace-insensitive comparison:
 * lowercases, collapses internal whitespace runs to a single space, and trims.
 * Returns '' for null/undefined so callers can compare safely.
 */
export const normalizeName = (name: string | null | undefined): string =>
  (name ?? '').toLowerCase().replace(/\s+/g, ' ').trim()

export const rejectWithTimeLimit = async <T>(
  timoutMs: number,
  task: Promise<T>,
  onReject?: () => void,
): Promise<T> => {
  let timeout
  // `never` so racing it against `task` resolves to T, sparing callers an `unknown` cast.
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeout = setTimeout(() => {
      onReject && onReject()
      reject('ERR_TIMEOUT')
    }, timoutMs)
  })

  try {
    return await Promise.race([task, timeoutPromise])
  } finally {
    // `finally` (not a post-await check) so the timer is cleared even when `task`
    // rejects — a plain check after `await Promise.race(...)` never runs in that case,
    // leaking a `timoutMs`-long timer for every rejected task.
    if (timeout) {
      clearTimeout(timeout)
    }
  }
}

export const checkIsLoanStructureComplete = (clientInfo: {
  loanType?: string | null
  arAdvance?: number | null
  pastInvoiceThreshold?: number | null
  crossAge?: number | null
  inventoryAdvanceRateCost?: number | null
  nolv?: number | null
  inventoryAdvanceRateNolv?: number | null
}) => {
  if (!clientInfo) {
    return false
  }

  let isARComplete = true
  let isInventoryComplete = true
  if (
    clientInfo.loanType !== LOAN_TYPES.inventory &&
    (isEmpty(clientInfo.arAdvance) ||
      isEmpty(clientInfo.pastInvoiceThreshold) ||
      isEmpty(clientInfo.crossAge))
  ) {
    isARComplete = false
  }
  if (
    clientInfo.loanType !== LOAN_TYPES.ar &&
    isEmpty(clientInfo.inventoryAdvanceRateCost) &&
    (isEmpty(clientInfo.nolv) || isEmpty(clientInfo.inventoryAdvanceRateNolv))
  ) {
    isInventoryComplete = false
  }

  return isARComplete && isInventoryComplete
}

// Utility function to get keys with type inference
export const getTypedObjectKeys = <T extends object>(obj: T): Array<keyof T> => {
  return Object.keys(obj) as Array<keyof T>
}

export const omitTypedKeys = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> => {
  return omit(obj, keys) as Omit<T, K>
}

export const cleanWebsiteForLogo = (website: string | null | undefined): string | null => {
  if (!website) return null
  return website.replace(/^(https?:\/\/)?(www\.)?/, '')
}

export const getLogoDevUrl = (website: string | null | undefined): string => {
  if (!website) return ''
  const cleanedWebsite = cleanWebsiteForLogo(website)
  if (!cleanedWebsite) return ''
  return `https://${LOGO_DEV_IMAGE_HOST}/${cleanedWebsite}?token=${LOGO_DEV_PUBLIC_KEY}`
}

/**
 * Exact parsed-hostname match, not a substring one: `https://img.logo.dev.attacker.example/x.png`
 * and `https://img.logo.dev@attacker.example/x.png` both contain the host but resolve elsewhere,
 * and must not be handed the token or our origin.
 */
export const isLogoDevUrl = (url: string): boolean => {
  try {
    return new URL(url).hostname === LOGO_DEV_IMAGE_HOST
  } catch {
    return false
  }
}

// `https://img.logo.dev/null` and friends: a null website interpolated into the path upstream.
const MISSING_LOGO_DEV_DOMAINS = ['', 'null', 'undefined']

/**
 * Sets the current token on a Logo.dev URL, replacing any stored one: getLogoDevUrl saves URLs
 * already tokenised, so appending would request `?token=pk?token=pk` (a 401). Returns null for a
 * Logo.dev URL with no domain in its path, a guaranteed 4xx, so callers fall back to their
 * no-logo state. Any other value passes through.
 */
export const addLogoDevToken = (iconUrl: string | null | undefined): string | null | undefined => {
  if (!iconUrl || !isLogoDevUrl(iconUrl)) return iconUrl

  const logoDevUrl = new URL(iconUrl)
  const domain = logoDevUrl.pathname.replace(/^\/+/, '').trim().toLowerCase()
  if (MISSING_LOGO_DEV_DOMAINS.includes(domain)) return null

  logoDevUrl.searchParams.set('token', LOGO_DEV_PUBLIC_KEY)
  return logoDevUrl.toString()
}

type ActivityWithBoxSignFiles = {
  borrowingBase?: {
    files?: Array<{
      type?: string | null
      boxSignInfo?: { signStatus?: string | null } | null
    }> | null
  } | null
}

export const getActivityBoxSignLabel = (
  activity: ActivityWithBoxSignFiles,
): BoxSignStatusLabel | null => {
  const file = activity?.borrowingBase?.files?.find(
    (f) => f.type === 'Summary_Signed' && f.boxSignInfo,
  )
  const signStatus = file?.boxSignInfo?.signStatus as BoxSignStatus | undefined
  return signStatus ? (BOX_SIGN_STATUS_LABEL[signStatus] ?? null) : null
}

export const getFundingSendStatus = (
  activity: ActivityWithBoxSignFiles & {
    type: string
    status: string
    borrowingBase?: { status?: string | null } | null
  },
): TaskStatusValue | null => {
  if (
    activity.type !== ActivityType.Funding ||
    activity.status !== ActivityStatus.Complete ||
    activity.borrowingBase?.status === FundingRequestStatus.Sent
  ) {
    return null
  }
  return getActivityBoxSignLabel(activity) ?? MANUALLY_SEND_LABEL
}

/**
 * Derives a readable plain-text view from a task's structured (Draft.js)
 * description. Stored once on write in `tasks.description_text` and read by
 * every plain-text consumer (Asana sync, reminder email/push, activity log).
 * Unordered list items get a `•` prefix, ordered items a `1.`/`2.` counter
 * (reset on any non-ordered block); blocks are joined by newlines.
 */
export function deriveDescriptionText(
  description: { blocks?: Array<{ text: string; type?: string }> } | null,
): string | null {
  if (!description?.blocks?.length) return null
  let orderedIndex = 0
  const text = description.blocks
    .map((block) => {
      if (block.type === 'ordered-list-item') {
        orderedIndex += 1
        return `${orderedIndex}. ${block.text}`
      }
      orderedIndex = 0
      if (block.type === 'unordered-list-item') {
        return `• ${block.text}`
      }
      return block.text
    })
    .join('\n')
  return text || null
}

// Mention kinds that resolve to an actual person (and so can be notified).
// Other kinds (Client, Entity, BBC, …) are taggable but aren't users.
export const USER_MENTION_TYPES = new Set<string>([
  MentionType.InternalUser,
  MentionType.ClientUser,
])

/** One @mention as Draft.js stores it: an entity of type 'mention' carrying `{ mention }`. */
interface IMentionBearingContent {
  entityMap?: Record<
    string,
    { type?: string; data?: { mention?: { id?: string; type?: string; name?: string } } }
  >
}

/**
 * Every @mention in a Draft.js body that `keep` accepts, deduped by id and in
 * document order. The single place the entityMap is walked, so the extractors
 * below differ only by which kinds they want and what they carry out.
 */
function collectMentions<T>(
  content: IMentionBearingContent | null,
  keep: (type: string | undefined) => boolean,
  map: (mention: { id: string; type?: string; name: string }) => T,
): T[] {
  if (!content?.entityMap) return []
  const byId = new Map<string, T>()
  Object.values(content.entityMap).forEach((entity) => {
    if (entity?.type !== 'mention') return
    const mention = entity.data?.mention
    if (!mention?.id || byId.has(mention.id)) return
    if (keep(mention.type)) {
      byId.set(mention.id, map({ id: mention.id, type: mention.type, name: mention.name ?? '' }))
    }
  })
  return [...byId.values()]
}

/** The mention shape the id/name extractors hand back. */
const toIdAndName = ({ id, name }: { id: string; name: string }) => ({ id, name })

/**
 * Pull the deduped user ids of @mentions from Draft.js content, for notifying
 * the people tagged. Only user-kind mentions count — tagging a client or entity
 * must not notify it as a user. Legacy mentions predate the `type` field and
 * were always users, so a missing type is treated as a user.
 * Shared by comment submit (client) and description-mention notifications (server).
 */
export function extractMentionedUserIds(content: IMentionBearingContent | null): string[] {
  return extractUserMentions(content).map(({ id }) => id)
}

/**
 * The same selection rule as `extractMentionedUserIds`, but keeps each mention's
 * display name — for surfaces that have to *talk about* the people tagged ("Alice
 * isn't in this channel yet") rather than just notify them. Both functions share
 * one definition of which mention kinds are people, so they can't drift.
 */
export function extractUserMentions(
  content: IMentionBearingContent | null,
): Array<{ id: string; name: string }> {
  return collectMentions(content, (type) => !type || USER_MENTION_TYPES.has(type), toIdAndName)
}

/**
 * Pull the deduped team ids of @mentions from Draft.js content.
 *
 * Kept separate from `extractMentionedUserIds` rather than folded into it: a
 * team names a *set* of people, and the write paths have to expand it to its
 * current members before anything can be notified or followed. Returning team
 * ids alongside user ids would hand every existing caller a non-user id where
 * it expects a user. Unlike the user extractor there is no legacy allowance —
 * an untyped mention predates teams entirely and is always a person.
 */
export function extractMentionedTeamIds(content: IMentionBearingContent | null): string[] {
  return collectMentions(content, (type) => type === MentionType.Team, toIdAndName).map(
    ({ id }) => id,
  )
}

/**
 * Every @mention in Draft.js content, whatever it names.
 *
 * The counterpart to `extractUserMentions`: that one narrows to the kinds that
 * are people (so they can be notified), this one keeps client, entity and
 * organization tags too — they are meaningful context even though they name
 * nobody to notify. Both walk the same entityMap, so the traversal and the
 * legacy-mention rule live in one place and cannot drift.
 *
 * Legacy mentions predate the `type` field and were always users, so a missing
 * type is reported as `InternalUser`.
 */
export function extractAllMentions(content: IMentionBearingContent | null): IAtlasAIMention[] {
  return collectMentions(
    content,
    () => true,
    ({ id, type, name }) => ({ id, type: (type as MentionType) ?? MentionType.InternalUser, name }),
  )
}

/**
 * Parses a stringified amendment-detail value back into its typed shape.
 * Shared by the client (parseAmendmentDetails) and the server
 * (parseAmendmentValue), so both sides interpret a stored `newValue`
 * the same way.
 *
 * Returns:
 *  - `null` for empty / null / undefined input
 *  - `undefined` when a typed parse fails (caller can skip the field)
 *  - the parsed value otherwise
 *
 * Note: Number uses parseFloat (preserves decimals) since the client
 * needs the displayed value to round-trip. The server's apply path
 * (`parseAmendmentValue`) intentionally swaps in parseInt for Number
 * to truncate, matching its entity columns — that's a server-only
 * concern handled at the call site.
 */
export const parseFieldValue = (rawValue: string | null | undefined, type: FieldType): unknown => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return null
  switch (type) {
    case FieldType.Currency:
    case FieldType.Percent:
    case FieldType.Number: {
      const parsed = parseFloat(rawValue)
      return Number.isFinite(parsed) ? parsed : undefined
    }
    case FieldType.Date:
      return rawValue
    case FieldType.Boolean: {
      const normalized = rawValue.toLowerCase()
      if (normalized === 'true') return true
      if (normalized === 'false') return false
      return undefined
    }
    default: {
      // UpdateTerms uses JSON.stringify for object-shaped values, so try
      // parsing as JSON first; fall back to the raw string.
      try {
        return JSON.parse(rawValue)
      } catch {
        return rawValue
      }
    }
  }
}

/**
 * The amortization schedule (ListAmortization + its client cards) simulates
 * payments from the closing date until termination, so both date anchors must
 * exist — simulating from nulls turns moments into 'Invalid date' strings.
 * Live loans always carry both (required at create / derived from the term);
 * analysis-flow Draft proposals may still be empty, so gate on the data
 * rather than on the Draft status: a filled-in Draft renders fine.
 *
 * Typed as a predicate so callers get both anchors narrowed to `string`
 * after the guard.
 */
export const hasAmortizationScheduleAnchors = <
  T extends Pick<ITermLoan, 'closingDate' | 'terminationDate'>,
>(
  termLoan: T,
): termLoan is T & { closingDate: string; terminationDate: string } =>
  Boolean(termLoan.closingDate && termLoan.terminationDate)

/** The K/M/B unit a magnitude is shortened to, as `[divisor, suffix]`. */
const compactUnit = (abs: number): [number, string] =>
  abs >= 1_000_000_000
    ? [1_000_000_000, 'B']
    : abs >= 1_000_000
      ? [1_000_000, 'M']
      : abs >= 1_000
        ? [1_000, 'K']
        : [1, '']

const SIGNIFICANT_DIGITS = 4

/** Decimals that leave `scaled` at four significant digits: 8.75 -> 3, 312.5 -> 1. */
const significantDecimals = (scaled: number): number =>
  Math.max(0, SIGNIFICANT_DIGITS - Math.max(1, Math.floor(Math.log10(Math.abs(scaled))) + 1))

/**
 * Short-form currency at no more than 4 significant digits, and whether that is the exact amount.
 * 8750000 -> { text: '8.75M', exact: true }; 2500012 -> { text: '2.5M', exact: false }.
 * Exactness is decided by integer modulo on the input, never by comparing float output back.
 */
export const formatSignificantCompactPrice = (
  value: unknown,
): { text: string; exact: boolean } | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const num = Number(value)
  if (!Number.isFinite(num)) {
    return null
  }
  if (num === 0) {
    return { text: '0', exact: true }
  }

  const isWholeDollars = Number.isInteger(num)
  const abs = Math.abs(num)
  if (abs < 1_000) {
    return {
      text: isWholeDollars ? String(num) : num.toFixed(2),
      exact: isWholeDollars || Number.isInteger(num * 100),
    }
  }

  let [divisor, suffix] = compactUnit(abs)
  let decimals = significantDecimals(num / divisor)
  // Promote when rounding reaches the next unit: 999,999 -> 1M, never 1000K.
  if (Math.abs(Number((num / divisor).toFixed(decimals))) >= 1_000 && suffix !== 'B') {
    ;[divisor, suffix] = compactUnit(divisor * 1_000)
    decimals = significantDecimals(num / divisor)
  }

  const fixed = (num / divisor).toFixed(decimals)
  // Strip trailing zeros only after a decimal point: 2.500 -> 2.5, but 750 stays 750.
  const text = decimals > 0 ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed
  const exact = isWholeDollars && abs % (divisor / 10 ** decimals) === 0

  return { text: `${text}${suffix}`, exact }
}

/**
 * Condenses a number for the dense mobile table: `1234567` -> `1.2M`, `301700` -> `301.7K`.
 *
 * Deliberately NOT built on `formatAmount`, despite the overlap. That helper opens with
 * `if (!amount) return null`, so it returns null for a legitimate `0` — which is right
 * for the KPI badges its 38 callers use it in, where a zero should render as blank, and
 * wrong in a table cell, where `0` is a real value that has to read as "0". Changing it in
 * place would silently turn dozens of blank badges into "0.00" across those callers.
 *
 * Emits no currency symbol: the `$` keeps coming from the existing `genericSs.pricePrefix`
 * span at the call sites, so cell layout is unchanged.
 */
export const formatCompactNumber = (value: unknown, decimals = 1): string | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const num = Number(value)
  if (!Number.isFinite(num)) {
    return null
  }
  if (num === 0) {
    return '0'
  }

  const abs = Math.abs(num)
  const [divisor, suffix] = compactUnit(abs)

  const scaled = num / divisor
  // One decimal at every magnitude — "$301.7K", not "$302K". A rounded hundreds-of-
  // thousands figure loses real precision on a balance, and the extra two characters fit.
  // Sub-thousand values have no suffix and no decimal: "847" is already exact.
  // Trailing `.0` still goes, because 2.0M is just 2M.
  const formatted = scaled.toFixed(suffix ? decimals : 0).replace(/\.0+$/, '')

  return `${formatted}${suffix}`
}

/** Currency flavour of {@link formatCompactNumber}: one decimal below 100 of a unit. */
export const formatCompactPrice = (value: unknown): string | null => formatCompactNumber(value, 1)

/** Every month from `start` to `end`, both inclusive, as the start of each month in `format`. */
export const listMonthsBetween = (
  start: moment.MomentInput,
  end: moment.MomentInput,
  format = 'YYYY-MM-DD',
): string[] => {
  const months: string[] = []
  const cursor = moment(start).startOf('month')
  const last = moment(end).startOf('month')

  while (cursor.isSameOrBefore(last)) {
    months.push(cursor.format(format))
    cursor.add(1, 'month')
  }

  return months
}
